// Phiếu đáp án giấy: bố cục theo MÃ ĐỀ, chuẩn hoá ký tự, quy đổi sang đáp án nộp /submit.
// Thứ tự câu / số câu IN / hoán vị phương án & ý ĐS lấy từ GET /pmaker-advanced/exam/{slug}/layout
// — CÙNG NGUỒN với file Word xuất ra (backend exam_variants.layout) nên giấy luôn chấm đúng.
// Chuẩn hoá PHẢI khớp backend/services/answer_sheet.py (server đọc, học sinh sửa ở đây).
import { API_CONFIG } from '../../config/api.config'

export type SheetGroup = 'TN' | 'DS' | 'TL'
export const LETTERS = ['A', 'B', 'C', 'D']
const DS_LABELS = ['a', 'b', 'c', 'd']

export interface SheetRow {
  q: any
  group: SheetGroup
  number: number              // số câu IN trên đề/phiếu (theo phần hoặc liên tục)
  index: number               // thứ tự trong phần (1-based) — khoá ô phiếu, không đổi giữa các mã đề
  optionOrder: string[]       // TN: option_id theo thứ tự A→D đã in
  statementOrder: string[]    // ĐS: ý gốc nằm ở vị trí a,b,c,d đã in
}

export interface SheetExam {
  id: string
  slug: string
  title: string
  settings: any
  code: string | null         // mã đề đang dùng (null = đề gốc)
  codes: string[]             // các mã đề giáo viên đã tạo
  numbering: 'per_part' | 'continuous'
  groups: Record<SheetGroup, SheetRow[]>
  questionOrder: string[]     // thứ tự in toàn đề (gửi kèm khi nộp)
}

const parseJson = (v: any) => {
  if (typeof v !== 'string') return v
  try { return JSON.parse(v) } catch { return v }
}

export const cellKey = (row: Pick<SheetRow, 'group' | 'index'>) => `${row.group}:${row.index}`

// Đề đầy đủ (/exam/{slug}, có thể nặng vì ảnh + lời giải) đã tải ở trang làm bài → giữ lại dùng chung:
// mở "Nộp bằng phiếu giấy" / đổi mã đề chỉ còn tải bố cục (rất nhẹ).
const examCache = new Map<string, Promise<any>>()

export function primeSheetExam(slug: string, data: any) {
  if (!data) return
  const copy = typeof structuredClone === 'function' ? structuredClone(data) : JSON.parse(JSON.stringify(data))
  examCache.set(slug, Promise.resolve(copy))
}

function loadExam(base: string, slug: string): Promise<any> {
  let p = examCache.get(slug)
  if (!p) {
    p = fetch(base).then(async (res) => {
      if (!res.ok) throw new Error('Không tìm thấy bài thi hoặc bài thi chưa được mở.')
      const data = (await res.json())?.data
      if (!data) throw new Error('Không tải được đề.')
      return data
    })
    examCache.set(slug, p)
    p.catch(() => examCache.delete(slug))
  }
  return p
}

export async function fetchSheetExam(slug: string, ma?: string | null): Promise<SheetExam> {
  const base = `${API_CONFIG.python.apiUrl}/pmaker-advanced/exam/${slug}`
  const [data, layRes] = await Promise.all([loadExam(base, slug), fetch(`${base}/layout${ma ? `?ma=${encodeURIComponent(ma)}` : ''}`)])
  const lay = await layRes.json().catch(() => ({}))
  if (!layRes.ok) throw new Error(lay?.detail || 'Không tải được bố cục đề.')

  const byId = new Map<string, any>()
  for (const p of data.parts || []) {
    for (const q of p.questions || []) byId.set(String(q.id), { ...q, lua_chon: parseJson(q.lua_chon), examMetadata: parseJson(q.examMetadata) })
  }
  const groups: Record<SheetGroup, SheetRow[]> = { TN: [], DS: [], TL: [] }
  for (const r of lay.questions || []) {
    const g = r.group as SheetGroup
    const q = byId.get(String(r.question_id))
    if (!q || !(g in groups)) continue        // tự luận (TLU) không có trên phiếu
    groups[g].push({ q, group: g, number: r.number, index: groups[g].length + 1,
      optionOrder: r.option_order || [], statementOrder: r.statement_order || [] })
  }
  return {
    id: data.id, slug, title: data.title || lay.title || 'Đề trắc nghiệm', settings: data.settings || {},
    code: lay.code || null, codes: lay.codes || [], numbering: lay.numbering === 'continuous' ? 'continuous' : 'per_part',
    groups, questionOrder: (lay.questions || []).map((r: any) => String(r.question_id)),
  }
}

// ── Chuẩn hoá ô học sinh ghi (khớp services/answer_sheet.py) ──────────────────
export function normalizeCell(group: SheetGroup, raw: string): { value: string; ok: boolean } {
  if (group === 'TN') {
    const s = String(raw || '').trim().toUpperCase()
    return { value: s, ok: !s || LETTERS.includes(s) }
  }
  if (group === 'DS') {
    const s = String(raw || '').replace(/[\s,;.\-_/|]/g, '').toUpperCase().replace(/D/g, 'Đ').replace(/Ð/g, 'Đ')
    return { value: s, ok: !s || (s.length === 4 && /^[ĐS]{4}$/.test(s)) }
  }
  const s = String(raw || '').replace(/\s+/g, '').replace(/\./g, ',').replace(/−/g, '-')
  return { value: s, ok: !s || /^-?\d+(,\d+)?$/.test(s) }
}

/** Ô Đúng/Sai → 4 vị trí a–d (vị trí trống = "_"). CHỈ nhận Đ hoặc S: ký tự khác (OCR/AI đọc sai)
 *  bị bỏ thành trống, dropped=true để tô vàng cho học sinh tự chọn lại. Chuỗi rỗng = bỏ câu. */
export function dsSlots(raw: string): { value: string; dropped: boolean } {
  const s = String(raw || '').replace(/[\s,;.\-/|]/g, '').toUpperCase().replace(/D/g, 'Đ').replace(/Ð/g, 'Đ')
  if (!s.replace(/_/g, '')) return { value: '', dropped: false }
  const chars = [...s]
  let dropped = chars.length !== 4
  const slots = [0, 1, 2, 3].map((i) => {
    const c = chars[i]
    if (c === 'Đ' || c === 'S') return c
    if (c !== undefined && c !== '_') dropped = true
    return '_'
  })
  return { value: slots.join(''), dropped }
}

/** Ô trên phiếu (khoá cellKey) → đáp án gửi /pmaker-advanced/submit, quy theo hoán vị của mã đề. */
export function buildSubmitAnswers(exam: SheetExam, values: Record<string, string>): Record<string, any> {
  const answers: Record<string, any> = {}
  exam.groups.TN.forEach((row) => {
    const v = (values[cellKey(row)] || '').toUpperCase()
    const idx = LETTERS.indexOf(v)
    if (idx < 0) return
    // Đề mới: chữ cái trên GIẤY = vị trí trong thứ tự in → option_id (chấm theo id). Đề cũ: gửi chữ cái.
    answers[row.q.id] = row.optionOrder.length ? row.optionOrder[idx] : v
  })
  exam.groups.DS.forEach((row) => {
    const v = values[cellKey(row)] || ''
    if (!v) return
    const keys = row.statementOrder.length ? row.statementOrder : DS_LABELS
    const out: Record<string, boolean> = {}
    keys.forEach((k, i) => {
      if (v[i] === 'Đ') out[`${row.q.id}_statement_${k}`] = true
      else if (v[i] === 'S') out[`${row.q.id}_statement_${k}`] = false
    })
    answers[row.q.id] = out
  })
  exam.groups.TL.forEach((row) => {
    const v = values[cellKey(row)] || ''
    if (v) answers[row.q.id] = v
  })
  return answers
}

const isTrue = (v: any) => ['true', '1', 'đ', 'đúng'].includes(String(v ?? '').trim().toLowerCase())

/** Đáp án đúng viết THEO ĐÚNG TỜ ĐỀ đã in (chữ cái / Đ-S theo vị trí ý / số). r = kết quả server. */
export function correctOnSheet(row: SheetRow, r: any): string {
  if (row.group === 'TN') {
    const key = String(r?.dap_an_dung ?? '')
    const idx = row.optionOrder.indexOf(key)
    return idx >= 0 ? LETTERS[idx] : key
  }
  if (row.group === 'DS') {
    const keys = row.statementOrder.length ? row.statementOrder : DS_LABELS
    return keys.map((k) => (isTrue(r?.[`answer_${k}`]) ? 'Đ' : 'S')).join('')
  }
  return String(r?.dap_an_dung ?? '')
}

/** Lời giải theo đúng nhãn a,b,c,d ĐÃ IN (ý bị đảo thì nhãn đi theo vị trí in). */
export function solutionOnSheet(row: SheetRow, r: any): string {
  const parts: string[] = []
  if (row.group === 'DS') {
    const keys = row.statementOrder.length ? row.statementOrder : DS_LABELS
    keys.forEach((k, i) => { if (r?.[`solution_${k}`]) parts.push(`**${DS_LABELS[i]})** ${r[`solution_${k}`]}`) })
  }
  if (r?.loi_giai) parts.unshift(r.loi_giai)
  return parts.join('\n\n')
}

export const scale10 = (score: number, max: number) => (max > 0 ? Math.round((score / max) * 1000) / 100 : 0)
